import React, { useMemo } from "react";
import { createTheme, ThemeProvider, useTheme } from "@material-ui/core/styles";
import { getCrmColors } from "./crmColors";

// Scoped to the two reply libraries, including dialogs rendered through portals.
export function createRepliesTheme(theme) {
  if (theme.palette.type !== "dark") return { ...theme, replyPage: true };

  const focus = { outline: `2px solid ${getCrmColors(theme, "dark").focus}`, outlineOffset: 2 };
  const overrides = {
    MuiPaper: { root: { backgroundColor: getCrmColors(theme, "dark").surface, color: "#FFFFFF", colorScheme: "dark" }, outlined: { borderColor: getCrmColors(theme, "dark").border } },
    MuiDialog: { paper: { border: `1px solid ${getCrmColors(theme, "dark").border}`, borderRadius: 12 } },
    MuiPopover: { paper: { border: `1px solid ${getCrmColors(theme, "dark").border}`, borderRadius: 10 } },
    MuiMenuItem: { root: { "&:hover": { backgroundColor: getCrmColors(theme, "dark").hover }, "&$selected, &$selected:hover": { backgroundColor: getCrmColors(theme, "dark").selected }, "&$focusVisible": focus } },
    MuiIconButton: { root: { color: "#FFFFFF", "&:hover": { backgroundColor: getCrmColors(theme, "dark").hover }, "&$focusVisible": focus } },
    MuiListItemIcon: { root: { color: "#FFFFFF" } },
    MuiButton: { root: { borderRadius: 8, "&$focusVisible": focus }, outlined: { borderColor: getCrmColors(theme, "dark").border }, outlinedPrimary: { color: "#FFFFFF", borderColor: getCrmColors(theme, "dark").border, "&:hover": { borderColor: getCrmColors(theme, "dark").accent, backgroundColor: getCrmColors(theme, "dark").hover } }, containedPrimary: { backgroundColor: getCrmColors(theme, "dark").accent, color: getCrmColors(theme, "dark").background, boxShadow: "none", "&:hover": { backgroundColor: getCrmColors(theme, "dark").focus } } },
    MuiInputBase: { root: { color: "#FFFFFF" }, input: { "&::placeholder": { color: getCrmColors(theme, "dark").muted, opacity: 1 } } },
    MuiOutlinedInput: { root: { backgroundColor: getCrmColors(theme, "dark").inset, borderRadius: 8, "&:hover $notchedOutline": { borderColor: getCrmColors(theme, "dark").muted }, "&$focused $notchedOutline": { borderColor: getCrmColors(theme, "dark").accent } }, notchedOutline: { borderColor: getCrmColors(theme, "dark").border } },
    MuiInputLabel: { root: { color: "#FFFFFF" } },
    MuiFormLabel: { root: { color: "#FFFFFF" } },
    MuiSelect: { icon: { color: "#FFFFFF" } },
    MuiCheckbox: { root: { color: "#FFFFFF" } },
    MuiChip: { root: { backgroundColor: getCrmColors(theme, "dark").raised, color: "#FFFFFF" }, colorPrimary: { backgroundColor: getCrmColors(theme, "dark").accentSoft, color: "#FFFFFF" }, icon: { color: "#FFFFFF" }, deleteIcon: { color: "#FFFFFF" } },
    MuiDivider: { root: { backgroundColor: getCrmColors(theme, "dark").border } },
    MuiTooltip: { tooltip: { backgroundColor: getCrmColors(theme, "dark").raised, color: "#FFFFFF", border: `1px solid ${getCrmColors(theme, "dark").border}` } },
    MuiSkeleton: { root: { backgroundColor: getCrmColors(theme, "dark").raised } },
  };
  const mergedOverrides = { ...theme.overrides };
  Object.entries(overrides).forEach(([component, rules]) => {
    mergedOverrides[component] = { ...theme.overrides?.[component] };
    Object.entries(rules).forEach(([rule, value]) => {
      mergedOverrides[component][rule] = { ...theme.overrides?.[component]?.[rule], ...value };
    });
  });
  return createTheme({
    ...theme,
    replyPage: true,
    replyDark: true,
    palette: {
      ...theme.palette,
      primary: { ...theme.palette.primary, main: getCrmColors(theme, "dark").accent, contrastText: getCrmColors(theme, "dark").background },
      background: { ...theme.palette.background, default: getCrmColors(theme, "dark").background, paper: getCrmColors(theme, "dark").surface },
      text: { ...theme.palette.text, primary: "#FFFFFF", secondary: getCrmColors(theme, "dark").muted },
      divider: getCrmColors(theme, "dark").border,
      action: { ...theme.palette.action, active: "#FFFFFF", hover: getCrmColors(theme, "dark").hover, selected: getCrmColors(theme, "dark").selected },
    },
    scrollbarStyles: { "&::-webkit-scrollbar": { width: 8, height: 8 }, "&::-webkit-scrollbar-thumb": { backgroundColor: getCrmColors(theme, "dark").border, borderRadius: 8 } },
    overrides: mergedOverrides,
  });
}

export default function RepliesTheme({ children }) {
  const outerTheme = useTheme();
  const theme = useMemo(() => createRepliesTheme(outerTheme), [outerTheme]);
  return (
    <ThemeProvider theme={theme}>
      <div style={{ height: "calc(100% - 48px)", minHeight: 0, backgroundColor: theme.replyDark ? getCrmColors(theme, "dark").background : undefined, colorScheme: theme.replyDark ? "dark" : undefined }}>
        {children}
      </div>
    </ThemeProvider>
  );
}
